import { useBrandFontFamilies } from '@src/hooks/useBrandFontFamilies';
import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  archiveFamilyInFamilies,
  archiveVariantInFamilies,
  parseFontFamilies,
  parseFontStyles,
  renameFamilyInFamilies,
} from '@src/utility/fonts';
import { getUncoveredWorkspaceLanguages } from '@src/utility/fontScriptCoverage';
import { DEFAULT_FONT_STYLE_PRESETS } from '@src/utility/fontStyles';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import UploadIcon from '../../svgs/UploadIcon';
import Accordion from './Accordion';
import BrandKitCustomFontsDialog from './BrandKitCustomFontsDialog';
import ConfirmDeleteDialog from './ConfirmDeleteDialog';
import FontFamilyCard from './FontFamilyCard';
import FontStyleConfigRow from './FontStyleConfigRow';
import { useFontDropzone } from './utils/useBrandKitFontUpload';

import type { FontStyleOverride, FontVariant } from '@src/types.ts/story';

const BrandKitFonts = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();

  const branding = videoCreator.organization?.branding;

  /**
   * Brand-kit-visible families: archived variants and fully-archived families
   * stripped (so they don't leak into the title count, the Font Styles family
   * dropdown, or the cards section). Shared resolver — every surface that reads
   * brand fonts agrees on this set. See `useBrandFontFamilies`.
   *
   * NOTE: this is the *active-filtered* view — never persist it back to
   * `branding.fontFamilies`. The delete/rename handlers below read the full
   * parsed list so previously-archived variants survive the write.
   */
  const fontFamilies = useBrandFontFamilies();

  const fontStyleOverrides = useMemo(
    () => parseFontStyles(branding?.fontStyles),
    [branding?.fontStyles],
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [fontsToUpload, setFontsToUpload] = useState<File[] | null>(null);
  const [pendingDeleteFamilyId, setPendingDeleteFamilyId] = useState<
    string | null
  >(null);
  const [pendingDeleteVariant, setPendingDeleteVariant] = useState<{
    familyId: string;
    variant: FontVariant;
  } | null>(null);

  const { getRootProps, getInputProps, open } = useFontDropzone({
    onDrop: files => {
      setFontsToUpload(files);
      setIsDialogOpen(true);
    },
  });

  /**
   * Persist a single Font Style override onto the org's branding. The
   * array on disk is keyed by `key` ('title' / 'subtitle' / 'text') —
   * we update the existing entry when present, otherwise append.
   *
   * `branding.fontStyles` is a DatoCMS JSON field (reads return a parsed
   * array). `parseFontStyles` is kept as a defensive guard — it normalises
   * any non-array (legacy string / store intermediate state) to [] before we
   * call `.some` on it — the same path the read-side `fontStyleOverrides`
   * useMemo uses.
   */
  const saveFontStyle = async (next: FontStyleOverride) => {
    const organization = videoCreator.organization!;
    const existing = parseFontStyles(organization.branding?.fontStyles);
    organization.branding = {
      ...organization.branding!,
      fontStyles: existing.some(o => o.key === next.key)
        ? existing.map(o => (o.key === next.key ? next : o))
        : [...existing, next],
    };
    await datoClientStore.albumRepository?.update(organization);
  };

  /**
   * Base list the mutation handlers operate on. Prefer the FULL stored list
   * (so previously-archived variants/families survive the write); fall back to
   * the materialised legacy grouping (`fontFamilies`) when nothing structured
   * is persisted yet, which migrates the legacy `customFonts[]` into the
   * structured model on first edit.
   */
  const baseFamilies = () => {
    const stored = parseFontFamilies(
      videoCreator.organization?.branding?.fontFamilies,
    );
    return stored.length ? stored : fontFamilies;
  };

  const deleteFamily = async (familyId: string) => {
    const organization = videoCreator.organization!;
    const base = baseFamilies();
    const variantIds = new Set(
      base.find(f => f.id === familyId)?.variants.map(v => v.artifactId) ?? [],
    );
    const now = new Date().toISOString();
    // Soft-archive every variant in the family rather than dropping it from the
    // JSON. The read-side resolver hides fully-archived families, so the family
    // disappears from the panel either way — but archiving keeps the underlying
    // Dato asset referenceable by any already-saved video and keeps both delete
    // paths consistent.
    organization.branding = {
      ...organization.branding!,
      fontFamilies: archiveFamilyInFamilies(base, familyId, now),
      ...(organization.branding?.customFonts?.length && {
        customFonts: organization.branding.customFonts.map(f =>
          variantIds.has(f.artifactId) ? { ...f, archivedAt: now } : f,
        ),
      }),
    };
    await datoClientStore.albumRepository?.update(organization);
  };

  const deleteVariant = async (variant: FontVariant) => {
    const organization = videoCreator.organization!;
    const now = new Date().toISOString();
    organization.branding = {
      ...organization.branding!,
      fontFamilies: archiveVariantInFamilies(
        baseFamilies(),
        variant.artifactId,
        now,
      ),
      ...(organization.branding?.customFonts?.length && {
        customFonts: organization.branding.customFonts.map(f =>
          f.artifactId === variant.artifactId ? { ...f, archivedAt: now } : f,
        ),
      }),
    };
    await datoClientStore.albumRepository?.update(organization);
  };

  const renameFamily = async (familyId: string, newName: string) => {
    const organization = videoCreator.organization!;
    organization.branding = {
      ...organization.branding!,
      fontFamilies: renameFamilyInFamilies(baseFamilies(), familyId, newName),
    };
    await datoClientStore.albumRepository?.update(organization);
  };

  const familyCount = fontFamilies.length;
  const title = familyCount
    ? `Fonts (${familyCount} ${familyCount === 1 ? 'family' : 'families'})`
    : 'Fonts';

  return (
    <Accordion id="fonts" title={title}>
      {familyCount === 0 ? (
        <SUploadDropzone {...getRootProps()} onClick={open}>
          <input {...getInputProps()} />
          <UploadIcon width="28" height="28" />
          <SDropzoneText>
            <strong>Drop font files here</strong>
            <span>or click to upload · TTF, OTF, WOFF, WOFF2</span>
          </SDropzoneText>
        </SUploadDropzone>
      ) : (
        <SContent>
          <SSection>
            <SSectionHeader>
              <span>Font Families</span>
              <div {...getRootProps()}>
                <input {...getInputProps()} />
                <SHeaderButton onClick={open}>
                  <UploadIcon width="12" height="12" />
                  Upload
                </SHeaderButton>
              </div>
            </SSectionHeader>
            {fontFamilies.map(family => (
              <FontFamilyCard
                key={family.id}
                family={family}
                onDeleteFamily={id => setPendingDeleteFamilyId(id)}
                onDeleteVariant={variant =>
                  setPendingDeleteVariant({ familyId: family.id, variant })
                }
                onRenameFamily={renameFamily}
                // PLAT-11412 — workspace languages the family can't display.
                uncoveredLanguages={getUncoveredWorkspaceLanguages(
                  family.variants,
                  videoCreator.organization?.supportedLanguages,
                )}
              />
            ))}
          </SSection>

          <SDivider />

          <SSection>
            <SSectionHeader>
              <span>Default Text Presets</span>
            </SSectionHeader>
            <SSectionHint>
              Override the default font, weight, and size used by the Title /
              Subtitle / Text presets on Graphics → Text. Anything left
              untouched falls back to the Arbor default.
            </SSectionHint>
            {DEFAULT_FONT_STYLE_PRESETS.map(preset => (
              <FontStyleConfigRow
                key={preset.key}
                presetKey={preset.key}
                presetLabel={preset.label}
                override={fontStyleOverrides.find(o => o.key === preset.key)}
                defaults={{
                  fontFamily: preset.fontFamily,
                  fontWeight: preset.fontWeight,
                  fontSize: preset.fontSize,
                }}
                fontFamilies={fontFamilies}
                onSave={saveFontStyle}
              />
            ))}
          </SSection>
        </SContent>
      )}

      {isDialogOpen && (
        <BrandKitCustomFontsDialog
          onClose={() => {
            setIsDialogOpen(false);
            setFontsToUpload(null);
          }}
          fontsToUpload={fontsToUpload}
        />
      )}

      {pendingDeleteFamilyId &&
        (() => {
          const family = fontFamilies.find(f => f.id === pendingDeleteFamilyId);
          if (!family) return null;
          const affectedFontStyles = fontStyleOverrides.filter(
            o => o.familyId === pendingDeleteFamilyId,
          );
          const activeVariantCount = family.variants.filter(
            v => !v.archivedAt,
          ).length;
          return (
            <ConfirmDeleteDialog
              title={`Delete "${family.name}"?`}
              onCancel={() => setPendingDeleteFamilyId(null)}
              onConfirm={() => {
                // Close immediately for one-click responsiveness; the
                // delete updates MobX synchronously and persists in the
                // background (see the variant dialog for rationale).
                const id = pendingDeleteFamilyId;
                setPendingDeleteFamilyId(null);
                void deleteFamily(id);
              }}
            >
              <p>
                All {activeVariantCount} weight
                {activeVariantCount !== 1 ? 's' : ''} will be removed.
              </p>
              {affectedFontStyles.length > 0 && (
                <SRiskNote>
                  <strong>
                    Used in {affectedFontStyles.length} Font Style
                    {affectedFontStyles.length !== 1 ? 's' : ''}:
                  </strong>{' '}
                  {affectedFontStyles.map(o => o.key).join(', ')}. Those styles
                  will fall back to the Arbor default family.
                </SRiskNote>
              )}
            </ConfirmDeleteDialog>
          );
        })()}

      {pendingDeleteVariant &&
        (() => {
          const { familyId, variant } = pendingDeleteVariant;
          const family = fontFamilies.find(f => f.id === familyId);
          if (!family) return null;
          const affectedFontStyles = fontStyleOverrides.filter(
            o => o.familyId === familyId && o.fontWeight === variant.weight,
          );
          const activeVariantCount = family.variants.filter(
            v => !v.archivedAt,
          ).length;
          const isLastVariant = activeVariantCount <= 1;
          return (
            <ConfirmDeleteDialog
              title={`Delete "${family.name} ${variant.label}"?`}
              onCancel={() => setPendingDeleteVariant(null)}
              onConfirm={() => {
                // Close immediately so one click feels instant. The
                // delete mutates MobX synchronously (variant disappears
                // right away) and persists in the background. Awaiting
                // the network before closing left the dialog sitting
                // open during the round-trip, so users clicked twice.
                setPendingDeleteVariant(null);
                void deleteVariant(variant);
              }}
            >
              <p>This weight will be removed from the {family.name} family.</p>
              {isLastVariant && (
                <SRiskNote>
                  <strong>This is the last weight in the family.</strong>{' '}
                  Removing it will leave {family.name} with no available
                  weights.
                </SRiskNote>
              )}
              {affectedFontStyles.length > 0 && (
                <SRiskNote>
                  <strong>
                    Used in {affectedFontStyles.length} Font Style
                    {affectedFontStyles.length !== 1 ? 's' : ''}:
                  </strong>{' '}
                  {affectedFontStyles.map(o => o.key).join(', ')}. Those styles
                  will fall back to the closest available weight or to the Arbor
                  default family.
                </SRiskNote>
              )}
            </ConfirmDeleteDialog>
          );
        })()}
    </Accordion>
  );
});

const SUploadDropzone = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 140px;
  border: 1.5px dashed var(--border);
  border-radius: 10px;
  cursor: pointer;
  transition: var(--transition-normal);
  color: var(--text-muted);
  &:hover {
    border-color: var(--primary);
    color: var(--primary);
    background: color-mix(in srgb, var(--primary) 5%, transparent);
  }
`;

const SDropzoneText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  strong {
    color: var(--text-secondary);
    font-size: var(--text-sm);
    font-weight: var(--font-weight-semi-bold);
  }
  span {
    color: var(--text-muted);
    font-size: var(--text-xs);
  }
`;

const SContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const SSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;

  > span {
    color: var(--text-secondary);
    font-size: var(--text-xs);
    font-weight: var(--font-weight-semi-bold);
    letter-spacing: 0.5px;
  }
`;

const SHeaderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  background: none;
  border: 1px solid var(--border);
  border-radius: 4px;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  padding: 4px 10px;
  cursor: pointer;
  transition: var(--transition-normal);
  &:hover {
    border-color: var(--text-muted);
    color: var(--foreground);
  }
`;

const SDivider = styled.div`
  height: 1px;
  background: var(--border);
`;

const SSectionHint = styled.p`
  margin: 0 0 8px;
  color: var(--text-muted);
  font-size: var(--text-xs);
  line-height: 1.5;
`;

const SRiskNote = styled.p`
  background: color-mix(in srgb, var(--warning, #f59e0b) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--warning, #f59e0b) 30%, transparent);
  border-radius: 6px;
  padding: 10px 12px;
  color: var(--text-secondary) !important;
  font-size: var(--text-xs) !important;
  strong {
    color: var(--foreground);
  }
`;

export default BrandKitFonts;
